import { authFetch } from '../utils/authFetch';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

function parseSseBlock(block) {
  let event = 'message';
  const dataLines = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return { event, data: dataLines.join('\n') };
  }
}

export async function streamAssistantChat({ messages, onEvent, signal }) {
  const response = await authFetch('/assistant/chat', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ messages }),
    signal,
  });

  if (response.status === 401) {
    throw new Error('unauthorized');
  }
  if (response.status === 503) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.message || 'assistant_unconfigured');
    err.code = 'unconfigured';
    throw err;
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || `assistant_http_${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const parsed = parseSseBlock(part.trim());
      if (parsed) onEvent(parsed);
    }
  }

  const tail = parseSseBlock(buffer.trim());
  if (tail) onEvent(tail);
}

async function readJson(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `assistant_http_${response.status}`);
  }
  return body.data ?? body;
}

export async function saveAssistantReport({ title, payload }) {
  const response = await authFetch('/assistant/reports', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ title, payload }),
  });
  return readJson(response);
}

export async function listAssistantReports() {
  const response = await authFetch('/assistant/reports');
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `assistant_http_${response.status}`);
  }
  return {
    items: Array.isArray(body.data) ? body.data : [],
    retentionDays: body.retentionDays ?? 0,
  };
}

export async function getAssistantReport(id) {
  const response = await authFetch(`/assistant/reports/${id}`);
  return readJson(response);
}

export async function deleteAssistantReport(id) {
  const response = await authFetch(`/assistant/reports/${id}`, { method: 'DELETE' });
  await readJson(response);
}
